// modules/clientes/components/ClienteForm.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, CircularProgress, Box,
  Grid, MenuItem, Divider, Typography,
  Stepper, Step, StepLabel,
  Autocomplete, Checkbox, FormControlLabel,
} from "@mui/material";
import * as clienteService from "../services/clienteService";
import * as grupoService   from "../../grupos/services/grupoService";
import axios from "../../../config/axiosConfig";
import { useAuth }         from "../../../context/AuthContext";
import { PAGO_OPTIONS }    from "../constants/pago";

const DEPARTAMENTOS = [
  "La Paz", "Cochabamba", "Santa Cruz", "Oruro",
  "Potosí", "Chuquisaca", "Tarija", "Beni", "Pando",
];
const MONEDAS    = [{ value: 1, label: "Dólares" }, { value: 2, label: "Bolivianos" }];
const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz"];
const TIPOS_SERV = ["Nacional", "Sucursal"];
const PASOS      = ["Datos del cliente", "Contacto principal", "Servicio"];

const PU_LABELS_BY_SERVICIO = {
  1: {
    pu_1: "P.U. Portatil",
    pu_2: "P.U. Movil",
    pu_4: "P.U. Base",
    pu_3: "P.U. Servicio",
  },
  2: {
    pu_1: "P.U. Alquiler",
    pu_2: "P.U. Servicio Tecnico",
    pu_4: "P.U. Servicio Nacional",
    pu_3: "P.U. Servicio Internacional",
  },
};

const getPuLabels = (codServicio) => PU_LABELS_BY_SERVICIO[Number(codServicio)] || null;

const CLIENTE_INI = {
  razon_social: "", tipo_persona: "", ci: "",
  primer_nombre: "", segundo_nombre: "",
  apellido_paterno: "", apellido_materno: "",
  rubro: "Servicios en General", direccion: "", telefono: "",
  celular: "", fax: "", pagina_web: "", correo_ofi: "",
  departamento: "", observaciones: "",
};

const CONTACTO_INI = {
  titulo            : "Señor",
  nombre            : "",
  paterno           : "",
  materno           : "",
  ci                : "",
  cargo_contacto    : "",
  telefono_contacto : "",
  celular           : "",
  correo            : "",
  departamento      : "",
};
const SERVICIO_INI = {
  grupo: "",
  cod_servicio: "", nombre: "", descripcion: "", tiempo_conservacion_recorrido: "", ejecutivo_venta: "",
  pago: "vencido", moneda: 2, sucursal_facturar: "",
  tipo: "Nacional", correo: "", observacion: "",
  estado: "Activo",
  central_riesgo: "No",
  pu_1: "", pu_2: "", pu_3: "", pu_4: "",
};

const RUBROS = [
  "Entidad Bancaria",
  "Financiera",
  "Transporte Valores",
  "Transporte Internacional-Nacional",
  "Flotas y Buses",
  "Seguridad",
  "Distribuidora",
  "Eventos y Publicidad",
  "Institucion Gubernamental",
  "Constructora",
  "Consulado o Embajada",
  "Doctores Hospital",
  "Import/Export",
  "Servicios en General",
];

const TITULOS = [
  "Señor", "Señora",
  "Licenciado", "Licenciada",
  "Ingeniero", "Ingeniera",
  "Doctor", "Doctora",
];

const FORM_COMPACT_SX = {
  pt: 1,
  "& .MuiTextField-root .MuiInputBase-root:not(.MuiInputBase-multiline)": {
    minHeight: 36,
  },
  "& .MuiInputBase-input": {
    fontSize: "0.82rem",
  },
  "& .MuiInputBase-input:not(textarea)": {
    py: "7px",
  },
  "& .MuiSelect-select": {
    py: "7px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.82rem",
  },
};

const PreciosServicioFields = ({ codServicio, values, onChange, disabled }) => {
  const labels = getPuLabels(codServicio);
  if (!labels) return null;

  return (
    <>
      {Object.entries(labels).map(([name, label]) => (
        <Grid key={name} size={{ xs: 6, sm: 3 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={label}
            name={name}
            value={values[name] ?? ""}
            onChange={onChange}
            disabled={disabled}
            inputProps={{ min: 0, step: "0.01" }}
          />
        </Grid>
      ))}
    </>
  );
};

const GrupoAutocomplete = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    if (!open) return;

    let activo = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await grupoService.buscar(inputValue.trim());
        if (activo) setOptions(Array.isArray(data) ? data : []);
      } catch {
        if (activo) setOptions([]);
      } finally {
        if (activo) setLoading(false);
      }
    }, 250);

    return () => {
      activo = false;
      clearTimeout(timer);
    };
  }, [open, inputValue]);

  const setGrupo = (nombre) => {
    const limpio = nombre || "";
    setInputValue(limpio);
    onChange({ target: { name: "grupo", value: limpio } });
  };

  return (
    <Autocomplete
      freeSolo
      size="small"
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      inputValue={inputValue}
      value={value || ""}
      filterOptions={(x) => x}
      onInputChange={(_, newInputValue) => setGrupo(newInputValue)}
      onChange={(_, newValue) => {
        setGrupo(typeof newValue === "string" ? newValue : newValue?.nombre || "");
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option?.nombre || ""
      }
      isOptionEqualToValue={(option, selected) =>
        (option?.nombre || option) === (selected?.nombre || selected)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Grupo"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const ClienteForm = ({ open, onClose, onSuccess, catalogos = {} }) => {
  const { user } = useAuth();
  const usernameActual = user?.username?.trim() || "";
  const sucursalUsuario = user?.sucursal?.trim() || "";
  const departamentoDefault = DEPARTAMENTOS.includes(sucursalUsuario) ? sucursalUsuario : "";
  const sucursalFacturarDefault = SUCURSALES.includes(sucursalUsuario) ? sucursalUsuario : "";

  const [paso,          setPaso]         = useState(0);
  const [cliente,       setCliente]      = useState({ ...CLIENTE_INI, departamento: departamentoDefault });
  const [contacto,      setContacto]     = useState(CONTACTO_INI);
  const [servicio,      setServicio]     = useState({ ...SERVICIO_INI, sucursal_facturar: sucursalFacturarDefault });
  const [loading,       setLoading]      = useState(false);
  const [error,         setError]        = useState(null);
  const [ejecutivos,    setEjecutivos]   = useState([]);
  const [loadingEjecutivos, setLoadingEjecutivos] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    if (departamentoDefault) {
      setCliente((p) => p.departamento ? p : ({ ...p, departamento: departamentoDefault }));
    }
    if (sucursalFacturarDefault) {
      setServicio((p) => p.sucursal_facturar ? p : ({ ...p, sucursal_facturar: sucursalFacturarDefault }));
    }
    if (usernameActual) {
      setServicio((p) => p.ejecutivo_venta ? p : ({ ...p, ejecutivo_venta: usernameActual }));
    }
  }, [open, departamentoDefault, sucursalFacturarDefault, usernameActual]);

  useEffect(() => {
    if (!open || !sucursalUsuario) {
      setEjecutivos([]);
      return;
    }

    let activo = true;
    const cargarEjecutivos = async () => {
      setLoadingEjecutivos(true);
      try {
        const { data } = await axios.get("/api/usuarios-trs", {
          params: { sucursal: sucursalUsuario, estado: "A" },
        });
        if (activo) setEjecutivos(Array.isArray(data) ? data : []);
      } catch {
        if (activo) setEjecutivos([]);
      } finally {
        if (activo) setLoadingEjecutivos(false);
      }
    };

    cargarEjecutivos();
    return () => { activo = false; };
  }, [open, sucursalUsuario]);

  const handleCliente  = (e) => setCliente((p)  => ({ ...p, [e.target.name]: e.target.value }));
  const handleContacto = (e) => setContacto((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleServicio = (e) => setServicio((p) => ({ ...p, [e.target.name]: e.target.value }));

  const autocompletarContactoPrincipal = () => {
    const nombreCompleto = [cliente.primer_nombre, cliente.segundo_nombre]
      .map((valor) => (valor || "").trim())
      .filter(Boolean)
      .join(" ");

    setContacto((prev) => ({
      ...prev,
      titulo: cliente.tipo_persona || prev.titulo || "Señor",
      nombre: prev.nombre || nombreCompleto,
      paterno: prev.paterno || (cliente.apellido_paterno || "").trim(),
      materno: prev.materno || (cliente.apellido_materno || "").trim(),
      ci: prev.ci || (cliente.ci || "").trim(),
    }));
  };

  const handleSiguiente = () => {
    setError(null);
    if (paso === 0) {
      if (!cliente.razon_social) return setError("Razón Social es requerida");
      if (!cliente.rubro) return setError("Rubro o Actividad es requerido");
      if (!cliente.departamento) return setError("El departamento es requerido");
      autocompletarContactoPrincipal();
    }
    if (paso === 1) {
      if (!contacto.titulo) return setError("El título es requerido");
      if (!contacto.nombre) return setError("El nombre es requerido");
      if (!contacto.paterno) return setError("El apellido paterno es requerido");
      if (!contacto.departamento) return setError("El departamento es requerido");
    }
    if (paso < 2) setPaso((p) => p + 1);
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    if (!servicio.cod_servicio) return setError("Debe seleccionar un tipo de servicio");
    if (!servicio.descripcion)  return setError("La descripción del servicio es requerida");
    if (!servicio.sucursal_facturar) return setError("La sucursal a facturar es requerida");
    submittingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const servicioPayload = { ...servicio };
      servicioPayload.grupo = (servicio.grupo || "").trim();
      servicioPayload.nombre = (servicio.nombre || "").trim();

      // El backend crea cliente, contacto y servicio en una sola transaccion.
      await clienteService.create({ cliente, contacto, servicio: servicioPayload });

      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || "Error al guardar");
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPaso(0);
    setCliente({ ...CLIENTE_INI, departamento: departamentoDefault });
    setContacto(CONTACTO_INI);
    setServicio({ ...SERVICIO_INI, sucursal_facturar: sucursalFacturarDefault });
    setError(null);
    submittingRef.current = false;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Nuevo Cliente</DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={FORM_COMPACT_SX}>
          <Stepper activeStep={paso} sx={{ mb: 2 }}>
            {PASOS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Paso 0: Datos del cliente */}
          {paso === 0 && (
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth required size="small" label="Razón Social"
                  error={!!error && !cliente.razon_social}
                  name="razon_social" value={cliente.razon_social} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth size="small" label="Tipo persona"
                  name="tipo_persona" value={cliente.tipo_persona} onChange={handleCliente} disabled={loading}>
                  <MenuItem value="">Sin tipo</MenuItem>
                  {TITULOS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="C.I."
                  name="ci" value={cliente.ci} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Primer nombre"
                  name="primer_nombre" value={cliente.primer_nombre} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Segundo nombre"
                  name="segundo_nombre" value={cliente.segundo_nombre} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Apellido paterno"
                  name="apellido_paterno" value={cliente.apellido_paterno} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Apellido materno"
                  name="apellido_materno" value={cliente.apellido_materno} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth required size="small" label="Rubro o Actividad"
                  error={!!error && !cliente.rubro}
                  name="rubro" value={cliente.rubro} onChange={handleCliente} disabled={loading}>
                  <MenuItem value="">Seleccionar...</MenuItem>
                  {RUBROS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth required size="small" label="Departamento"
                  error={!!error && !cliente.departamento}
                  name="departamento" value={cliente.departamento} onChange={handleCliente} disabled={loading}>
                  <MenuItem value="">Seleccionar...</MenuItem>
                  {DEPARTAMENTOS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" label="Dirección"
                  name="direccion" value={cliente.direccion} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" label="Teléfono"
                  name="telefono" value={cliente.telefono} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" label="Celular"
                  name="celular" value={cliente.celular} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" label="Fax"
                  name="fax" value={cliente.fax} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 6, sm: 6 }}>
                <TextField fullWidth size="small" label="Correo Oficial" type="email"
                  name="correo_ofi" value={cliente.correo_ofi} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 6, sm: 6 }}>
                <TextField fullWidth size="small" label="Página Web"
                  name="pagina_web" value={cliente.pagina_web} onChange={handleCliente} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" multiline rows={2} label="Observaciones"
                  name="observaciones" value={cliente.observaciones} onChange={handleCliente} disabled={loading} />
              </Grid>
            </Grid>
          )}

          {/* Paso 1: Contacto */}
          {paso === 1 && (
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">
                  Datos del contacto principal del cliente.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField select fullWidth required size="small" label="Título" 
                  error={!!error && !contacto.titulo}
                  name="titulo" value={contacto.titulo} onChange={handleContacto} disabled={loading}>
                  <MenuItem value="">Sin título</MenuItem>
                  {TITULOS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth required size="small" label="Nombre" 
                  error={!!error && !contacto.nombre}
                  name="nombre" value={contacto.nombre} onChange={handleContacto} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth required size="small" label="Apellido Paterno" 
                  error={!!error && !contacto.paterno}
                  name="paterno" value={contacto.paterno} onChange={handleContacto} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="Apellido Materno"
                  name="materno" value={contacto.materno} onChange={handleContacto} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" label="C.I."
                  name="ci" value={contacto.ci} onChange={handleContacto} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" label="Cargo"
                  name="cargo_contacto" value={contacto.cargo_contacto} onChange={handleContacto} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField select fullWidth required size="small" label="Departamento"
                  error={!!error && !contacto.departamento}
                  name="departamento" value={contacto.departamento} onChange={handleContacto} disabled={loading}>
                  <MenuItem value="">Seleccionar...</MenuItem>
                  {DEPARTAMENTOS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" label="Teléfono"
                  name="telefono_contacto" value={contacto.telefono_contacto} onChange={handleContacto} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" label="Celular"
                  name="celular" value={contacto.celular} onChange={handleContacto} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="Correo" type="email"
                  name="correo" value={contacto.correo} onChange={handleContacto} disabled={loading} />
              </Grid>
            </Grid>
          )}

          {/* Paso 2: Servicio */}
          {paso === 2 && (
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <GrupoAutocomplete
                  value={servicio.grupo}
                  onChange={handleServicio}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="Nombre"
                  name="nombre" value={servicio.nombre} onChange={handleServicio} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth required size="small" label="Descripción"
                  error={!!error && !servicio.descripcion}
                  name="descripcion" value={servicio.descripcion} onChange={handleServicio} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" label="Tiempo de Conservación de Recorrido"
                  name="tiempo_conservacion_recorrido" value={servicio.tiempo_conservacion_recorrido}
                  onChange={handleServicio} disabled={loading} />
              </Grid>
              {false && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth required size="small" label="Descripción"
                  error={!!error && !servicio.descripcion}
                  name="descripcion" value={servicio.descripcion} onChange={handleServicio} disabled={loading} />
              </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth required size="small" label="Tipo de Servicio"
                  error={!!error && !servicio.cod_servicio}
                  name="cod_servicio" value={servicio.cod_servicio} onChange={handleServicio} disabled={loading}>
                  <MenuItem value="">Seleccionar...</MenuItem>
                  {(catalogos.servicios || []).map((s) => (
                    <MenuItem key={s.id_servicio} value={s.id_servicio}>{s.nombre}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <PreciosServicioFields
                codServicio={servicio.cod_servicio}
                values={servicio}
                onChange={handleServicio}
                disabled={loading}
              />
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  size="small"
                  disabled={loading}
                  options={ejecutivos}
                  loading={loadingEjecutivos}
                  value={
                    ejecutivos.find((u) => u.username === servicio.ejecutivo_venta) ||
                    (servicio.ejecutivo_venta ? { username: servicio.ejecutivo_venta, nombre: servicio.ejecutivo_venta } : null)
                  }
                  onChange={(_, value) => {
                    setServicio((p) => ({ ...p, ejecutivo_venta: value?.username || "" }));
                  }}
                  getOptionLabel={(option) =>
                    option?.nombre ? `${option.nombre} (${option.username})` : option?.username || ""
                  }
                  isOptionEqualToValue={(option, value) => option.username === value.username}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ejecutivo de Ventas"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingEjecutivos && <CircularProgress size={14} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField select fullWidth size="small" label="Forma de Pago"
                  name="pago" value={servicio.pago} onChange={handleServicio} disabled={loading}>
                  {PAGO_OPTIONS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField select fullWidth size="small" label="Moneda"
                  name="moneda" value={servicio.moneda} onChange={handleServicio} disabled={loading}>
                  {MONEDAS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField select fullWidth required size="small" label="Sucursal a Facturar"
                  error={!!error && !servicio.sucursal_facturar}
                  name="sucursal_facturar" value={servicio.sucursal_facturar} onChange={handleServicio} disabled={loading}>
                  <MenuItem value="">Seleccionar...</MenuItem>
                  {SUCURSALES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField select fullWidth size="small" label="Tipo de Servicio"
                  name="tipo" value={servicio.tipo} onChange={handleServicio} disabled={loading}>
                  {TIPOS_SERV.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" label="Correo Electrónico" type="email"
                  name="correo" value={servicio.correo} onChange={handleServicio} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" multiline rows={2} label="Observación"
                  name="observacion" value={servicio.observacion} onChange={handleServicio} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={servicio.central_riesgo === "Cliente Reportado"}
                      onChange={(e) => setServicio((p) => ({
                        ...p, central_riesgo: e.target.checked ? "Cliente Reportado" : "No",
                      }))}
                      disabled={loading}
                    />
                  )}
                  label="Central de Riesgo"
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          {paso > 0 && (
            <Button onClick={() => { setError(null); setPaso((p) => p - 1); }} disabled={loading}>
              Atrás
            </Button>
          )}
          {paso < 2 ? (
            <Button variant="contained" onClick={handleSiguiente} disabled={loading}>
              Siguiente
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}>
              {loading ? "Guardando..." : "Crear Cliente"}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ClienteForm;
